import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import useMessageDisplay from '../components/MessageDisplay.js';
import { signIn, signOut } from '../reducers/initContext.js';
import { parseDate } from '../utils/dateUtils.js';
import { decodeJWT } from '../utils/generalUtils.js';
import { deepEquals } from '../utils/objectUtilities.js';
import useAppState, { useNetworkState } from './appStateHook.js';
import { CustomEqualityMap } from '../utils/customEqualityMap.js';
import { useNavigate } from './navigateHook.js';
import { addEntityDelta, removeEntityDelta } from '../reducers/pendingUpdateSlice.js';
import useClientConfig from './clientConfigHook.js';
import { fetch } from '../utils/fetch.js';
import { useApp } from '../components/AppProvider.js';

export const QueryContext = createContext();
export const useQueryStatus = () => useContext(QueryContext);

let refresh = null;

const makeQueryToken = async (App, dispatch, navigate, token, refreshToken) => {
    if (!token) {
        return null;
    }
    if (refresh) {
        return refresh;
    }
    const now = new Date();
    const { nbf, exp }= decodeJWT(token);
    if (refreshToken && ((exp && parseDate(exp * 1000) < now) || (nbf && parseDate(nbf * 1000) > now))) {
        refresh = new Promise(f => {
            fetch('/refreshToken', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken })
            }).then(async response => {
                if (!response.ok) {
                    signOut(App, dispatch, navigate);
                    f(null);
                }
                const { token: newToken } = await response.json();
                signIn(App, dispatch, token);
                f(newToken);
            }, () => f(null));
        });
        return refresh;
    }
    return token;
}

export const runNetworkRequest = async (App, dispatch, navigate, flags, token, refreshToken, { url, method, body }) => {
    try {
        const options = {
            method: method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: await makeQueryToken(App, dispatch, navigate, token, refreshToken)
            }
        };
        if (body) {
            options.body = JSON.stringify(body);
        }
        const response = await fetch(url, options);
        if (!flags?.ignore) {
            if (response.ok) {
                try {
                    const data = await response.json();
                    return { success: true, data };
                } catch (err) {
                    return { success: false, data: { status: 200, statusText: err, url: response.url, timestamp: new Date() }};
                }
            } else if (response.status === 401 || response.status === 403) {
                signOut(App, dispatch, navigate);
                return { success: false, data: { status: response.status, statusText: 'Security Violation', url: response.url, timestamp: new Date() } };
            } else {
                return { success: false, data: { status: response.status, statusText: response.statusText, url: response.url, timestamp: new Date()} }
            }
        }
    } catch (e) {
        return { success: null, data: { error: e, url, timestamp: new Date() }}
    }
}

export function useNetworkWithFlags() {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { isOnline } = useNetworkState();
    const { token, refreshToken } = useAppState();
    const App = useApp();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { beginQuery, endQuery } = useQueryStatus();
    const messageDisplay = useMessageDisplay();
    const canRequest = useRef(() => !clientConfig.enableOfflineMode || isOnline);

    useEffect(() => {
        canRequest.current = !clientConfig.enableOfflineMode || isOnline
    }, [isOnline, clientConfig.enableOfflineMode])

    const makeOps = useCallback(flags => {
        const doNetworkOperation = async (flags, url, method, body, { successAction, failureAction, eagerMeta }) => {
            const meta = undefined;
            const tools = { t, ...messageDisplay };
            const queryID = beginQuery({ url, method });
            if (eagerMeta) {
                if (successAction && successAction.hasAction !== false && !successAction.type) {
                    throw new Error('A success action without a type for dispatch has been found!')
                }
                if (failureAction && failureAction.hasAction !== false && !failureAction.type) {
                    throw new Error('A failure action without a type for dispatch has been found!')
                }
                dispatch(addEntityDelta(queryID, eagerMeta.entity, eagerMeta.pk, eagerMeta.delta ?? body, successAction?.type, failureAction?.type, url, method, eagerMeta.delta ? body : undefined ));
            }
            if (canRequest.current) {
                const result = await runNetworkRequest(App, dispatch, navigate, flags, token, refreshToken, { url, method, body });
                endQuery(queryID)
                if (result?.success) {
                    if (successAction) {
                        const toDispatch = successAction(result.data, meta, tools);
                        if (toDispatch) {
                            dispatch(toDispatch);
                        }
                    }
                    if (eagerMeta) {
                        dispatch(removeEntityDelta(queryID, eagerMeta.entity, eagerMeta.pk));
                    }
                    return result.data;
                } else if (result?.success === false) {
                    if (failureAction) {
                        const toDispatch = failureAction(result.data, meta, tools);
                        if (toDispatch) {
                            dispatch(toDispatch);
                        }
                    }
                    if (eagerMeta) {
                        dispatch(removeEntityDelta(queryID, eagerMeta.entity, eagerMeta.pk));
                    }
                    return null;
                }
            } else {
                endQuery(queryID);
            }
            return undefined
        };
        // NOTE: there is a special network proxy in the reducer utilities that handles aggregate queries
        // any changes made to this API must by synchronized with changes to that proxy
        return {
            get: (url, successAction, failure, { eagerMeta } = {}) => {
                return doNetworkOperation(flags, url, 'GET', undefined, { successAction, failureAction: failure, eagerMeta });
            },
            put: (url, data, successAction, failure, { eagerMeta } = {}) => {
                return doNetworkOperation(flags, url, 'PUT', data, { successAction, failureAction: failure, eagerMeta });
            },
            post: (url, data, successAction, failure, { eagerMeta } = {}) => {
                return doNetworkOperation(flags, url, 'POST', data, { successAction, failureAction: failure, eagerMeta });
            },
            delete: (url, successAction, failure, { eagerMeta } = {}) => {
                return doNetworkOperation(flags, url, 'DELETE', undefined, { successAction, failureAction: failure, eagerMeta });
            },
            dispatch: async (action) => {
                dispatch(action);
            },
            postData: async (url, body, headers) => {
                // Replace with your endpoint URL and any required headers
                const response = await fetch((window.APP_HOST ?? '') + url, { 
                    method: 'POST',
                    body: body,
                    headers: {
                        Authorization: await makeQueryToken(App, dispatch, navigate, token, refreshToken),
                        ...headers
                    },
                })
                if (!response.ok) {
                    throw new Error('Error posting ' + url + ' status = ' + response.status + ' ' + response.statusText)
                }
                return response.json();
            },
            fetchData: async (url, options) => {
                const response = await fetch((window.APP_HOST ?? '') + url, { ...options, headers: { Authorization: await makeQueryToken(App, dispatch, navigate, token, refreshToken) } });
                if (!response.ok) {
                    throw new Error('Error fetching ' + url + ' status = ' + response.status + ' ' + response.statusText);
                }
                return response;
            },
            dataURIToBlob(dataURL) {
                // Split the data URL at the comma to get the MIME type and the Base64 string
                const parts = dataURL.split(',');
                const mimeType = parts[0].split(':')[1].split(';')[0]; // Extract MIME type
                const base64 = parts[1]; // Encoded Base64 string
            
                // Decode Base64 string
                const decodedData = atob(base64);
            
                // Create Uint8Array from decoded data
                const bytes = new Uint8Array(decodedData.length);
                for (let i = 0; i < decodedData.length; i++) {
                    bytes[i] = decodedData.charCodeAt(i);
                }
            
                // Create Blob from the Uint8Array
                return new Blob([bytes], { type: mimeType });
            },
            downloadBlob: (blob, filename) => {
                if (window.ReactNativeWebView) {
                    const a = new FileReader();
                    a.onload = function(e) {
                        if (window.APP_VERSION > 1) {
                            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'downloadBlob', data: { filename, url: e.target.result } }));
                        } else {
                            window.ReactNativeWebView.postMessage(JSON.stringify({ event: 'downloadBlob', data: { filename, url: e.target.result } }));
                        }
                    }
                    a.readAsDataURL(blob);
                } else {
                    // Create a URL for the blob
                    const url = URL.createObjectURL(blob);
                
                    // Create a new anchor element
                    const a = document.createElement('a');
                
                    // Set the href and download attributes for the anchor element
                    a.href = url;
                    a.download = filename || 'download';
                
                    // Append the anchor to the body,
                    // trigger click to download, and then remove the anchor
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                
                    // Clean up by revoking the object URL
                    URL.revokeObjectURL(url);
                }
            }
        }
    }, [dispatch, navigate, t, beginQuery, endQuery, messageDisplay, token, refreshToken, App])


    return makeOps;
}

class CustomSet {
    constructor(equalityFunction) {
      this.items = [];
      this.equalityFunction = equalityFunction;
    }
  
    has(element) {
      return this.items.some(item => this.equalityFunction(item, element));
    }
  
    add(element) {
      if (!this.has(element)) {
        this.items.push(element);
      }
    }
  
    delete(element) {
      const index = this.items.findIndex(item => this.equalityFunction(item, element));
      if (index !== -1) {
        this.items.splice(index, 1);
        return true;
      }
      return false;
    }
  
    clear() {
      this.items = [];
    }
  
    size() {
      return this.items.length;
    }
  
    values() {
      return [...this.items];
    }
  }
  
class Deferred {
    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}

export const aggregatingFetch = (fetchSingle, fetchMultiple, { primitiveKey = false } = {}) => {
    const pending = primitiveKey ? new Map() : new CustomEqualityMap((a, b) => deepEquals(a, b));
    let pendingTimer = null;
    return (...key) => network => {
        if (pending.has(key)) {
            return pending.get(key).promise;
        }
        if (pendingTimer) {
            clearTimeout(pendingTimer);
            pendingTimer = null;
        }
        const deferred = new Deferred();
        pending.set(key, deferred);
        pendingTimer = setTimeout(() => {
            // note we have to localize the keys and deferred values to be processed because
            // another timeout could fire while we are waiting for the server to respond and we
            // can't allow the two timeouts to interfere with eacy other
            if (pending.size === 1) {
                const key = pending.keys().next().value
                const deferred = pending.get(key);
                pending.delete(key);
                fetchSingle(...key)(network).then(
                    (...args) => {
                        deferred.resolve(...args);
                    },
                    (...args) => {
                        deferred.reject(...args);
                    }
                );
            } else {
                const keys = Array.from(pending.keys());
                const deferred = keys.map(key => pending.get(key));
                keys.forEach(key => pending.delete(key));
                fetchMultiple(key.length <= 1 ? keys.flat() : keys)(network).then(
                    (...args) => {
                        deferred.forEach(deferred => deferred.resolve(...args));
                    },
                    (...args) => {
                        deferred.forEach(deferred => deferred.reject(...args));
                    }
                );
            }
        }, 400)
        return deferred.promise;
    };
}

export const unifyingFetch = (fetchSingle, fetchMultiple) => {
    const pending = new CustomSet((a, b) => deepEquals(a, b));
    let pendingTimer = null;

    // Wrapper for fetchMultiple to handle batch fetching with a timer
    const fetchMultipleWrapper = (network) =>
        new Promise((resolve, reject) => {
            if (pendingTimer) {
                clearTimeout(pendingTimer); // Cancel the existing timer
                pendingTimer = null;
            }

            // Directly fetch all records
            fetchMultiple()(network).then(resolve, reject);

            // Clear pending keys since a fetch is happening now
            pending.clear();
        });

    const fetchSingleWrapper = (network) =>
        new Promise((resolve, reject) => {
            if (pendingTimer) {
                clearTimeout(pendingTimer); // Reset the timer
            }

            // Set a timer to handle the batch
            pendingTimer = setTimeout(() => {
                // Handle batch based on the number of keys
                if (pending.size() === 1) {
                    fetchSingle(...pending.values()[0])(network).then(resolve, reject);
                } else {
                    fetchMultiple()(network).then(resolve, reject);
                }

                // Clear the pending keys and timer
                pending.clear();
                pendingTimer = null;
            }, 400);
        });

    return (...key) => {
        // Add the current key to the pending set
        pending.add(key);

        // If a timer is already running, use the fetchMultiple wrapper
        if (pendingTimer) {
            return fetchMultipleWrapper;
        }

        // Return a function to handle the dispatch logic
        return fetchSingleWrapper;
    };
};

export default function useNetwork() {
    const network = useNetworkWithFlags();
    return useMemo(() => network(undefined), [network]);
}